/* eslint-disable no-param-reassign */
/**
 * Redux slice for the payout approval dashboard.
 *
 * State holds the list of pending SubmissionApproval records returned by
 * GET /submissions/approvals/pending. Records are replaced in bulk on each
 * fetch (approvalsLoaded), patched in-place when stamped (approvalUpdated),
 * or removed when payment completes or the approval is rejected (approvalRemoved).
 *
 * This slice is NOT persisted — the dashboard always fetches fresh on mount.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SubmissionApproval } from 'shared/typings/approvals';

interface ApprovalsState {
  data: SubmissionApproval[];
  total: number;
}

const initialState: ApprovalsState = {
  data: [],
  total: 0,
};

type ApprovalsAction<T = ApprovalsState> = PayloadAction<{ data: T }>;

export const approvalsSlice = createSlice({
  name: 'approvals',
  initialState,
  reducers: {
    approvalsLoaded: (state, action: ApprovalsAction<SubmissionApproval[]>) => {
      state.data = action.payload.data;
      state.total = action.payload.data.length;
    },
    approvalUpdated: (state, action: ApprovalsAction<SubmissionApproval>) => {
      const idx = state.data.findIndex((a) => a.id === action.payload.data?.id);
      if (idx !== -1) {
        state.data.splice(idx, 1, action.payload.data);
      }
    },
    approvalRemoved: (state, action: ApprovalsAction<Pick<SubmissionApproval, 'id'>>) => {
      const before = state.data.length;
      state.data = state.data.filter((a) => a.id !== action.payload.data.id);
      state.total -= before - state.data.length;
    },
  },
});

export const { approvalsLoaded, approvalUpdated, approvalRemoved } = approvalsSlice.actions;

export const selectApprovals = (state: { approvals: ApprovalsState }) => state.approvals.data;
